import React, { useState, useEffect, useRef, useContext } from "react";
import socket from "../../socket";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { TiPinOutline } from "react-icons/ti";
import { TiPin } from "react-icons/ti";
import { PiColumnsPlusRight } from "react-icons/pi";
import { IoMdClose } from "react-icons/io";
import DatePicker from "react-datepicker";
import "../../styles/projects/Projects.css";
import { useAuth } from "../Authentication/AuthContext";
import { useRolePermissions } from "../Settings/RoleContext";
import { FaChevronDown } from "react-icons/fa";
import api from "../../api/axiosConfig";
import { ProjectContext } from "../../App";
import { ToastContainer, toast } from "react-toastify";
import ExcelJS from "exceljs";
import { motion } from "framer-motion";
import PhaseStatusSelector from "./PhaseStatusSelector";

const WorkingProgressList = ({ searchFilter, fetchData, groupBy }) => {
  const [projects, setProjects] = useState([]);
  const [editedDetRemarks, setEditedDetRemarks] = useState({});
  const [editedDesRemarks, setEditedDesRemarks] = useState({});
  const [showScope, setShowScope] = useState({});
  const projectRef = useRef(null);
  const tableRef = useRef(null);
  const stageRef = useRef(null);
  const { isNavBarOpen } = useContext(ProjectContext);
  const { userDetails } = useAuth();
  const { hasPermission } = useRolePermissions();
  const userID = userDetails.id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusOptions, setStatusOption] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    if (searchFilter && typeof searchFilter === "string") {
      setFilter(searchFilter.toLowerCase());
    } else {
      setFilter(""); // Handle cases where searchFilter is missing or invalid
    }
  }, [searchFilter]);
  const [projectToDelete, setProjecttoDelete] = useState([]);
  const tableHeading = [
    "Project No",
    "Project",
    "Phase",
    "Customer",
    "Scope",
    "Team",
    "Tags",
    "Remarks (DO)",
    "Scheduled(DO)",
    "Issued On(DO)",
    "Remarks (DE)",
    "Scheduled(DE)",
    "Issued On(DE)",
    "VO(UC)",
    "VO PHrs(UC)",
    "VO PHrs",
    "VO AHrs",
    "Planned Hrs",
    "Actual Hrs",
    "Difference",
    "DE PHrs",
    "DE AHrs",
    "DO PHrs",
    "DO AHrs",
    "Priority",
    "Manager",
    "Start Date",
    "Due Date",
    "INV %",
  ];
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [activeProjLink, setProjActiveLink] = useState(() => {
    return localStorage.getItem("activeproject") || "activeprojects";
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(() => {
    return Number(localStorage.getItem("projectsPerPage")) || 25;
  });
  const [stickyColumns, setStickyColumns] = useState(() => {
    const storedColumns = localStorage.getItem("workinprogressstickyColumns");
    return storedColumns ? JSON.parse(storedColumns) : ["Project No"];
  });

  useEffect(() => {
    // Store the stickyColumns in localStorage whenever it updates
    localStorage.setItem(
      "workinprogressstickyColumns",
      JSON.stringify(stickyColumns)
    );
  }, [stickyColumns]);

  const toggleStickyColumn = (heading) => {
    setStickyColumns((prev) => {
      if (prev.length === 1 && prev.includes(heading)) return prev;

      const updatedColumns = prev.includes(heading)
        ? prev.filter((col) => col !== heading) // Remove from sticky
        : [...prev, heading]; // Add to sticky

      return updatedColumns;
    });
  };

  useEffect(() => {
    localStorage.setItem("projectsPerPage", projectsPerPage.toString());
  }, [projectsPerPage]);

  const calculatePaginationCount = (projectsPerPage, currentPage) => {
    const start = (currentPage - 1) * projectsPerPage + 1;
    const end = start + projectsPerPage - 1;
    return `${start}-${end}`;
  };
  const paginationCount = calculatePaginationCount(
    projectsPerPage,
    currentPage
  );

  const [visibleColumns, setVisibleColumns] = useState({
    "Project No": true,
    Project: true,
    Phase: true,
    Customer: true,
    Scope: true,
    Team: true,
    Tags: true,
    "Remarks (DO)": true,
    "Scheduled(DO)": true,
    "Issued On(DO)": true,
    "Remarks (DE)": true,
    "Scheduled(DE)": true,
    "Issued On(DE)": true,
    "VO(UC)": true,
    "VO PHrs(UC)": true,
    "VO PHrs": true,
    "VO AHrs": true,
    "Planned Hrs": true,
    "Actual Hrs": true,
    Difference: true,
    "DE PHrs": true,
    "DE AHrs": true,
    "DO PHrs": true,
    "DO AHrs": true,
    Priority: true,
    Manager: true,
    "Start Date": true,
    "Due Date": true,
    "INV %": true,
  });
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    column: null,
  });
  const [columns, setColumns] = useState(tableHeading);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);

  useEffect(() => {
    socket.on("connect", () => {});

    socket.on("new phase added", (newPhase) => {
      const fetchNewData = async () => {
        try {
          // Fetch project list
          const projectListUrl =
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Super Admin"
              ? "/api/project/workinprogresslist"
              : `/api/project/workinprogress/list/${userDetails.id}`;
          const projectDetailsUrl =
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Super Admin"
              ? `/api/project/workinprogressdetails`
              : `/api/project/workinprogress/details/${userDetails.id}`;
          const projectListRes = await api.get(projectListUrl);
          const projectList = projectListRes.data;

          // Fetch project details
          const projectDetailsRes = await api.get(projectDetailsUrl);
          const projectDetails = projectDetailsRes.data;

          // Filter the project matching the new phase ID
          let projectFilterByStatus = projectList
            .filter((item) => item.id === Number(newPhase.id))
            .map((item) => {
              const matchingDetails = projectDetails.find(
                (detail) => detail.phase_id === item.id
              );

              return {
                ...item,
                ...matchingDetails, // Merge matching phase details
                overall_actual_hours:
                  matchingDetails?.overall_actual_hours || "00:00",
                design_budget_hours:
                  matchingDetails?.design_budget_hours || "00:00",
                detailing_budget_hours:
                  matchingDetails?.detailing_budget_hours || "00:00",
                design_actual_hours:
                  matchingDetails?.design_actual_hours || "00:00",
                detailing_actual_hours:
                  matchingDetails?.detailing_actual_hours || "00:00",
                difference_hours: matchingDetails?.difference_hours || "00:00",
              };
            });

          // Append the new project to the existing state
          setProjects((prev) => [
            ...(prev || []),
            ...(Array.isArray(projectFilterByStatus)
              ? projectFilterByStatus
              : [projectFilterByStatus]),
          ]);
        } catch (error) {
          setError(error);
          console.error("Error fetching project data:", error.message || error);
        }
      };
      if (newPhase) {
        fetchNewData();
      }
    });

    socket.on("phase details updated", (updatedPhase) => {
      const fetchNewData = async (updatedPhase) => {
        if (!updatedPhase || !updatedPhase.id) {
          console.error("updatedPhase is not defined or missing an ID");
          return;
        }

        try {
          // Fetch project list
          const projectListUrl =
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Super Admin"
              ? "/api/project/workinprogresslist"
              : `/api/project/workinprogress/list/${userDetails.id}`;
          const projectDetailsUrl =
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Super Admin"
              ? `/api/project/workinprogressdetails`
              : `/api/project/workinprogress/details/${userDetails.id}`;
          const projectListRes = await api.get(projectListUrl);
          const projectList = projectListRes.data;

          // Fetch project details
          const projectDetailsRes = await api.get(projectDetailsUrl);
          const projectDetails = projectDetailsRes.data;

          // Find the matching project
          const newlyEditedPhase = projectList.find(
            (ph) => ph.id === Number(updatedPhase.id)
          );

          if (!newlyEditedPhase) return; // Ensure the phase exists

          // Find corresponding details using phase_id
          const phaseDetails = projectDetails.find(
            (detail) => detail.phase_id === newlyEditedPhase.id
          );

          // Merge phase details into the project data
          const updatedPhaseData = {
            ...newlyEditedPhase,
            ...phaseDetails, // Merge matching phase details
            overall_actual_hours: phaseDetails?.overall_actual_hours || "00:00",
            design_budget_hours: phaseDetails?.design_budget_hours || "00:00",
            detailing_budget_hours:
              phaseDetails?.detailing_budget_hours || "00:00",
            design_actual_hours: phaseDetails?.design_actual_hours || "00:00",
            detailing_actual_hours:
              phaseDetails?.detailing_actual_hours || "00:00",
            difference_hours: phaseDetails?.difference_hours || "00:00",
          };

          // Update state with new data
          setProjects((prev) =>
            prev.map((p) =>
              p.id === updatedPhaseData.id ? updatedPhaseData : p
            )
          );
        } catch (error) {
          setError(error);
          console.error("Error fetching project data:", error.message || error);
        }
      };
      if (updatedPhase) {
        fetchNewData(updatedPhase);
      }
    });

    socket.on("phase updated", (updatedPhase) => {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === Number(updatedPhase.id) // Ensure you're matching the correct ID
            ? {
                ...project,
                phaseno: updatedPhase.phaseno,
                title: updatedPhase.title,
                start_date: updatedPhase.formattedStartDate,
                end_date: updatedPhase.formattedEndDate,
              }
            : project
        )
      );
    });
    socket.on("phase deleted", (data) => {
      // Ensure that `phaseIds` is an array
      const idsArray = Array.isArray(data.phaseIds)
        ? data.phaseIds
        : [data.phaseIds];

      setProjects((prevProjects = []) =>
        prevProjects.filter((project) => !idsArray.includes(project.id))
      );
    });

    socket.on("phase_stage_updated", ({ phaseID, stage }) => {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === Number(phaseID) // Ensure you're matching the correct ID
            ? {
                ...project,
                phase_scopes: stage,
              }
            : project
        )
      );
    });

    socket.on("phase field updated", (data) => {
      setProjects((prevProjects) => {
        const updatedProjects = prevProjects.map((proj) =>
          proj.id === Number(data.phaseID)
            ? { ...proj, [data.field]: data.formattedValue }
            : proj
        );

        return updatedProjects;
      });
    });

    return () => {
      socket.off("new phase added");
      socket.off("phase details updated");
      socket.off("phase deleted");
      socket.off("phase updated");
      socket.off("phase_stage_updated");
      socket.off("phase field updated");
    };
  }, []);
  // Function to save user preferences to the backend
  const saveUserPreferences = async (preferences) => {
    const userId = userID; // Replace with the actual logged-in user's ID

    try {
      const response = await api.post(
        "/api/workinprogress/save-column-preferences",
        {
          userId,
          preferences,
        }
      );
    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  const fetchUserPreferences = async (userID) => {
    try {
      const response = await api.get(
        `/api/workinprogress/get-column-preferences/${userID}`
      );
      return response.data; // Axios automatically parses JSON responses
    } catch (error) {
      console.error("Error fetching preferences:", error);
      return null;
    }
  };

  const [isFormVisible, setIsFormVisible] = useState(false);
  const toggleFormVisibility = () => {
    setIsFormVisible((prevState) => !prevState);
    updateColumnPositions(stickyColumns);
  };
  //function for column visibility
  const toggleColumnVisibility = (columnKey) => {
    // Count how many columns are currently visible
    const visibleColumnCount =
      Object.values(visibleColumns).filter(Boolean).length;

    // Prevent hiding the last visible column
    if (visibleColumnCount === 1 && visibleColumns[columnKey]) {
      toast.error("At least one column must be visible", {
        position: "bottom-center",
        autoClose: 2000,
      });
      return; // Do nothing if the user tries to hide the last remaining column
    }
    const updatedVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    };

    // Update the state with the new visibility
    setVisibleColumns(updatedVisibleColumns);

    // Call the API to save the updated preferences
    saveUserPreferences(updatedVisibleColumns);
  };

  const handleRightClick = (event, column) => {
    event.preventDefault();
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      column: column,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the context menu
      if (stageRef.current && !stageRef.current.contains(event.target)) {
        setShowScope(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const hideColumn = async () => {
    if (contextMenu.column) {
      const updatedColumns = {
        ...visibleColumns,
        [contextMenu.column]: false,
      };
      setVisibleColumns(updatedColumns);
      setContextMenu({ ...contextMenu, visible: false });
      saveUserPreferences(updatedColumns);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the context menu
      if (projectRef.current && !projectRef.current.contains(event.target)) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    };

    // Attach event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu]);

  const handleDragStart = (index) => {
    setDraggedColumnIndex(index);
  };

  const handleDrop = (dropIndex) => {
    if (draggedColumnIndex !== null) {
      // Clone columns array and reorder it
      const updatedColumns = [...columns];
      const [draggedColumn] = updatedColumns.splice(draggedColumnIndex, 1);
      updatedColumns.splice(dropIndex, 0, draggedColumn);

      // Update the columns in state
      setColumns(updatedColumns);

      // Update visibility order to match the new column order
      const newVisibleColumns = {};
      updatedColumns.forEach((col) => {
        newVisibleColumns[col] = visibleColumns[col];
      });
      setVisibleColumns(newVisibleColumns);

      // Save preferences to persist updated order and visibility
      saveUserPreferences(newVisibleColumns);

      // Reset draggedColumnIndex
      setDraggedColumnIndex(null);
    }
  };

  const updateColumnPositions = (stickyCols = stickyColumns) => {
    if (!tableRef.current) return;

    const table = tableRef.current;
    const thead = table.querySelector("thead");

    if (thead) {
      thead.style.position = "sticky";
      thead.style.top = "-1px";
      thead.style.zIndex = "5";
      thead.style.backgroundColor = "white";
    }

    const headers = table.querySelectorAll("th");
    const rows = table.querySelectorAll("tr");

    let leftOffset = 0; // Start position for sticky columns
    const lastColumnIndex = headers.length - 2; // Exclude last column (assuming it has no header)

    headers.forEach((header, index) => {
      const colName = header.innerText.trim();

      if (
        (stickyCols.includes(colName) || index === 0) &&
        index < lastColumnIndex
      ) {
        // ✅ Sticky columns (excluding last column)
        header.style.position = "sticky";
        header.style.left = `${leftOffset}px`;
        header.style.zIndex = "4";
        header.style.backgroundColor = "white";

        leftOffset += header.offsetWidth; // Move to next column position
      } else if (index < lastColumnIndex) {
        // ✅ Reset styles when unpinned (excluding last column)
        header.style.position = "";
        header.style.left = "";
        header.style.zIndex = "";
        header.style.backgroundColor = "";
      }
    });

    rows.forEach((row) => {
      const cells = row.querySelectorAll("td");

      leftOffset = 0; // Reset for row

      cells.forEach((cell, index) => {
        const colName = headers[index]?.innerText.trim();

        if (
          (stickyCols.includes(colName) || index === 0) &&
          index <= lastColumnIndex
        ) {
          // ✅ Sticky columns (except last functional column)
          cell.style.position = "sticky";
          cell.style.left = `${leftOffset}px`;
          cell.style.zIndex = "2";
          cell.style.backgroundColor = "inherit";

          leftOffset += cell.offsetWidth; // Align next sticky column
        } else {
          // ✅ Reset styles when unpinned
          cell.style.position = "";
          cell.style.left = "";
          cell.style.zIndex = "";
          cell.style.backgroundColor = "";
        }
      });
    });
  };

  useEffect(() => {
    updateColumnPositions(stickyColumns);
  }, [
    stickyColumns,
    projectsPerPage,
    projects,
    searchFilter,
    filter,
    sortConfig,
    fetchData,
    isNavBarOpen,
    visibleColumns,
    groupBy,
    currentPage,
  ]);

  //saved preferences
  useEffect(() => {
    // Fetch the user preferences when the component mounts
    const loadPreferences = async () => {
      const preferences = await fetchUserPreferences(userID);
      if (
        preferences &&
        Object.keys(visibleColumns).length === Object.keys(preferences).length
      ) {
        setVisibleColumns(preferences);
        setColumns(Object.keys(preferences));
      }
    };

    loadPreferences();
  }, [userID]);
  //saved preferences
  const [columnWidths, setColumnWidths] = useState(
    Array(12).fill(150) // assuming 9 columns in your table
  );

  const startX = useRef(null);
  const startWidth = useRef(null);
  const activeColumnIndex = useRef(null);

  const handleMouseDown = (index, e) => {
    startX.current = e.pageX;
    startWidth.current = columnWidths[index];
    activeColumnIndex.current = index;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (activeColumnIndex.current !== null) {
      let newWidth = startWidth.current + (e.pageX - startX.current);
      newWidth = Math.max(50, Math.min(newWidth, 500)); // Use reasonable limits
      setColumnWidths((prevWidths) => {
        const updatedWidths = [...prevWidths];
        updatedWidths[activeColumnIndex.current] = newWidth;
        return updatedWidths;
      });
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    activeColumnIndex.current = null;
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch project list
      const projectListUrl =
        userDetails.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin"
          ? "/api/project/workinprogresslist"
          : `/api/project/workinprogress/list/${userDetails.id}`;
      const projectDetailsUrl =
        userDetails.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin"
          ? `/api/project/workinprogressdetails`
          : `/api/project/workinprogress/details/${userDetails.id}`;
      const projectListRes = await api.get(projectListUrl);
      const projectList = projectListRes.data;

      // Fetch project details
      const projectDetailsRes = await api.get(projectDetailsUrl);
      const projectDetails = projectDetailsRes.data;

      // Fetch status options
      const statusOptionRes = await api.get("/api/status");
      setStatusOption(statusOptionRes.data);

      // Merge data based on matching IDs
      let mergedProjects = projectList
        .filter((item) => item.activestatus === 1)
        .map((item) => {
          const matchingDetails = projectDetails.find(
            (detail) => detail.phase_id === item.id
          );

          return {
            ...item,
            ...matchingDetails, // Merge matching details into project data
            overall_actual_hours:
              matchingDetails?.overall_actual_hours || "00:00",
            design_budget_hours:
              matchingDetails?.design_budget_hours || "00:00",
            detailing_budget_hours:
              matchingDetails?.detailing_budget_hours || "00:00",
            design_actual_hours:
              matchingDetails?.design_actual_hours || "00:00",
            detailing_actual_hours:
              matchingDetails?.detailing_actual_hours || "00:00",
            difference_hours: matchingDetails?.difference_hours || "00:00",
          };
        });

      setProjects(mergedProjects);
    } catch (error) {
      setError(error);
      console.error("Error fetching project data:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [userDetails.role, userID, activeProjLink]);

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );
  }

  const mapKeyToProjectProperty = (heading) => {
    switch (heading.trim()) {
      case "Project No":
        return "projectno";
      case "Project":
        return "name";
      case "Customer":
        return "customer_name";
      case "Team": // Fixed inconsistent casing
        return "associated_groups";
      case "Planned Hrs":
        return "overall_budget_hours";
      case "Actual Hours":
        return "actual_hours";
      case "Tags":
        return "tags";
      case "Phase":
        return "title";
      case "Manager":
        return "project_manager_names";
      case "Start Date":
        return "start_date"; // Fixed mismatch
      case "Due Date":
        return "end_date"; // Fixed mismatch
      case "INV %":
        return "phase_invoice_claimpercent";
      case "VO(UC)":
        return "unclaimed_variation_count";
      case "Actual Hrs":
        return "overall_actual_hours";
      case "DE AHrs":
        return "design_actual_hours";
      case "DE PHrs":
        return "design_budget_hours";
      case "DO AHrs":
        return "detailing_actual_hours";
      case "DO PHrs":
        return "detailing_budget_hours";
      case "VO PHrs(UC)":
        return "unclaimed_variation_hours";
      case "VO AHrs":
        return "total_variation_actual_hours";
      case "VO PHrs":
        return "total_variation_hours";
      case "S.No":
        return "index";
      case "Scheduled(DO)":
        return "scheduledate_do";
      case "Issued On(DO)":
        return "issued_on_do";
      case "Scheduled(DE)":
        return "scheduledate_de";
      case "Issued On(DE)":
        return "issued_on_de";
      case "Start Date":
        return "start_date";
      case "Due Date":
        return "end_date";
      case "Scope":
        return "phase_status";
      case "Remarks (DO)":
        return "remarks_det";
      case "Remarks (DE)":
        return "remarks_des";
      case "Priority":
        return "priority";
      case "Difference":
        return "difference_hours";
      default:
        return null;
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const tagOrder = [
    "Yet to Start",
    "On Progress",
    "Waiting",
    "On Hold",
    "Cancelled",
    "Completed",
    "Internal",
  ];
  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes; // Convert HH:MM to total minutes
  };
  const sortedProjects = [...projects].sort((a, b) => {
    const key = sortConfig?.key || "tags"; // Default sorting key is "tags"
    const direction = sortConfig?.direction === "asc" ? 1 : -1;

    // Step 1: If sorting by tags, sort the phase_tags directly
    if (key === "tags") {
      return (
        (tagOrder.indexOf(a.phase_tags) - tagOrder.indexOf(b.phase_tags)) *
        direction
      );
    }

    // Step 2: Otherwise, sort within the tag groups while keeping tag order fixed
    const aIndex = tagOrder.indexOf(a.phase_tags);
    const bIndex = tagOrder.indexOf(b.phase_tags);

    if (aIndex !== bIndex) {
      return aIndex - bIndex; // Always sort tags in ASCENDING order
    }

    // Step 3: Sort within the same tag group by the chosen key
    let aValue = a[key];
    let bValue = b[key];

    switch (key) {
      case "start_date":
      case "end_date":
      case "scheduledate":
      case "issued_on":
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
        break;
      case "unclaimed_variation_count": // Ensure numeric sorting
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
        break;
      case "total_variation_actual_hours":
      case "total_variation_hours":
      case "unclaimed_variation_hourss":
      case "actual_hours":
      case "overall_budget_hours":
      case "difference_hours":
      case "detailing_budget_hours":
      case "detailing_actual_hours":
      case "design_budget_hours":
      case "design_actual_hours":
        aValue = convertTimeToMinutes(aValue);
        bValue = convertTimeToMinutes(bValue);
        break;
      case "project_manager_names":
      case "customer_name":
      case "team":
      case "name":
      case "remarks":
      case "notes":
      case "priority":
        aValue = aValue?.toLowerCase() || "";
        bValue = bValue?.toLowerCase() || "";
        break;

      default:
        if (typeof aValue === "string") aValue = aValue.toLowerCase();
        if (typeof bValue === "string") bValue = bValue.toLowerCase();
        break;
    }
    if (aValue < bValue) return -1 * direction;
    if (aValue > bValue) return 1 * direction;
    return 0;
  });

  const filteredProjects = sortedProjects.filter((project) => {
    return (
      project.name?.toLowerCase().includes(filter) ||
      project.projectno?.toLowerCase().includes(filter) ||
      project.customer_name?.toLowerCase().includes(filter) ||
      project.phase_tags?.toLowerCase().includes(filter) ||
      project.project_manager_names?.toLowerCase().includes(filter) ||
      project.status?.toLowerCase().includes(filter) ||
      project.title?.toLowerCase().includes(filter)
    );
  });

  const handleSearch = (searchFilter) => {
    setFilter(searchFilter.toLowerCase());
  };

  const handleDeleteProject = (e, selectedProject) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setProjecttoDelete((prevSelected) => [...prevSelected, selectedProject]);
    } else {
      setProjecttoDelete((prevSelected) =>
        prevSelected.filter((project) => project.id !== selectedProject.id)
      );
    }
  };

  // Check if all columns are selected (visible)
  const isAllSelected = () => {
    return Object.values(visibleColumns).every((isVisible) => isVisible);
  };
  const handleProjectStatusChange = async (phaseID, value, field) => {
    let confirm;
    if (value === "Waiting" && field === "phase_tags") {
      confirm = window.confirm(
        "Do you want to raise an invoice for this project?"
      );
    } else if (value === "Completed" && field === "phase_tags") {
      confirm = window.confirm(
        "Do you want to raise an invoice for this project?"
      );
    }
    try {
      await api.put(`/api/phase-status/${phaseID}`, { value, field, confirm });
    } catch (error) {
      toast.error("Failed to update project", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  const handleProjectScopeChange = async (phaseID, updatedStatuses) => {
    try {
      await api.put(`/api/phasestage/${phaseID}`, { updatedStatuses });
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message); // Debugging Line
    }
  };

  const getColorValue = (value) => {
    let color;
    if (value === "Yet to Start") {
      color = "#ffffff";
    } else if (value === "On Hold") {
      color = "#e4d1b9";
    } else if (value === "On Progress") {
      color = "#f4f4f4";
    } else if (value === "Completed") {
      color = "#e6fce6";
    } else if (value === "Cancelled") {
      color = "#fce6e6";
    } else if (value === "Waiting") {
      color = "#f1ebd4";
    }
    return color;
  };
  const handleProjectUpdate = async (field, value, phaseID) => {
    const formattedValue =
      (field === "scheduledate_do" ||
        field === "issued_on_do" ||
        field === "scheduledate_de" ||
        field === "issued_on_de") &&
      value
        ? format(value, "yyyy-MM-dd")
        : value || null;
    try {
      const response = await api.put(`/api/workinprogress-update/${phaseID}`, {
        field,
        formattedValue,
        userID,
      });
    } catch (error) {
      toast.error("Failed to update project", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  const exportToExcel = (currentProjects) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Projects");

    // Define the columns based on your row data
    const columns = [
      { header: "Project No", key: "projectNo", width: 20 },
      { header: "Phase", key: "phase", width: 20 },
      { header: "Project", key: "projectName", width: 30 },
      { header: "Customer", key: "customerName", width: 20 },
      { header: "Scope", key: "phase_status", width: 20 },
      { header: "Team", key: "associated_groups", width: 20 },
      { header: "VO(UC)", key: "unclaimed_variation_count", width: 15 },
      { header: "VO PHrs(UC)", key: "unclaimed_variation_hours", width: 15 },
      { header: "VO AHrs", key: "total_variation_actual_hours", width: 15 },
      { header: "Planned Hrs", key: "budgetHours", width: 15 },
      { header: "Actual Hrs", key: "actualHours", width: 15 },
      { header: "Difference", key: "Difference", width: 15 },
      { header: "DE PHrs", key: "designBudgetHours", width: 15 },
      { header: "DE AHrs", key: "designActualHours", width: 15 },
      { header: "DO PHrs", key: "detailingBudgetHours", width: 15 },
      { header: "DO AHrs", key: "detailingActualHours", width: 15 },
      { header: "Tags", key: "tags", width: 20 },
      { header: "Priority", key: "priority", width: 15 },
      { header: "Manager", key: "projectManager", width: 20 },
      { header: "Start Date", key: "startDate", width: 12 },
      { header: "Due Date", key: "dueDate", width: 12 },
      { header: "INV %", key: "invoiceStatus", width: 20 },
      { header: "Description", key: "description", width: 30 },
      { header: "Scheduled(DO)", key: "scheduledate_do", width: 12 },
      { header: "Issued On(DE)", key: "issued_on_do", width: 12 },
      { header: "Scheduled(DE)", key: "scheduledate_de", width: 12 },
      { header: "Issued On(DO)", key: "issued_on_de", width: 12 },
      { header: "Remarks (DO)", key: "remarks_det", width: 30 },
      { header: "Remarks (DE)", key: "remarks_des", width: 30 },
    ];

    worksheet.columns = columns;

    // Add rows dynamically from the currentProjects data
    currentProjects.forEach((project) => {
      const row = {
        projectNo: project.projectno,
        phase: project.title,
        projectName: project.name,
        customerName: project.customer_name,
        phase_status: project.phase_scopes || "",
        associated_groups:
          Array.isArray(project.associated_groups) &&
          project.associated_groups.length > 0
            ? project.associated_groups.join(", ")
            : null,
        phasestatus: project.status,
        unclaimed_variation_count: project.unclaimed_variation_count,
        unclaimed_variation_hours: project.unclaimed_variation_hours,
        total_variation_actual_hours: project.total_variation_actual_hours,
        budgetHours: project.overall_budget_hours,
        actualHours: project.overall_actual_hours,
        Difference: project.difference_hours,
        designBudgetHours: project.design_budget_hours,
        designActualHours: project.design_actual_hours,
        detailingBudgetHours: project.detailing_budget_hours,
        detailingActualHours: project.detailing_actual_hours,
        tags: project.phase_tags,
        priority: project.priority || "",
        projectManager: project.project_manager_names,
        startDate: project.start_date
          ? format(new Date(project.start_date), "dd-MM-yyyy")
          : "",
        dueDate: project.end_date
          ? format(new Date(project.end_date), "dd-MM-yyyy")
          : "",
        invoiceStatus: `${project.phase_invoice_claimpercent}% Claimed`,
        // projectStatus: project.status_name,
        description: project.description || "",
        scheduledate_de: project.scheduledate_de
          ? format(new Date(project.scheduledate_de), "dd-MM-yyyy")
          : "",
        scheduledate_do: project.scheduledate_do
          ? format(new Date(project.scheduledate_do), "dd-MM-yyyy")
          : "",
        issued_on_do: project.issued_on_do
          ? format(new Date(project.issued_on_do), "dd-MM-yyyy")
          : "",
        issued_on_de: project.issued_on_de
          ? format(new Date(project.issued_on_de), "dd-MM-yyyy")
          : "",
        remarks_des: project.remarks_des || "",
        remarks_det: project.remarks_det || "",
      };

      worksheet.addRow(row);
    });

    // Apply styles for borders
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Save the file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "workinprogress.xlsx";
      link.click();
    });
  };

  const convertToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };
  const parseTimeToMinutes = (timeString) => {
    if (!timeString || timeString === "00:00") return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const renderRowData = (project) => {
    return columns
      .filter((columnKey) =>
        userDetails.role_name !== "Super Admin" &&
        userDetails.role_name !== "Administrator"
          ? columnKey !== "INV %"
          : true
      )
      .map((columnKey) => {
        if (visibleColumns[columnKey]) {
          switch (columnKey) {
            case "Project No":
              return (
                <td
                  key="projectNo"
                  style={{
                    backgroundColor: getColorValue(project.phase_tags),
                    display: "flex",
                  }}
                  title={
                    project.unclaimed_variation_count !== 0
                      ? project.unclaimed_variation_count
                      : ""
                  }
                >
                  <div
                    className={
                      hasPermission("projects", "delete")
                        ? "project-table-con"
                        : "project-table-con-user"
                    }
                    style={{
                      width: "125px",
                      marginTop: "8px",
                    }}
                  >
                    <Link
                      style={{
                        backgroundColor: "transparent",
                        color: "black",
                        top: "0px",
                      }}
                      to={`/project/phases/${project.project_id}`}
                      className="select-projectlist"
                      onClick={() =>
                        localStorage.setItem("activePLink", "phase")
                      }
                    >
                      {`${project.projectno}-${project.phaseno}`}
                    </Link>
                    {/* Add GA Badge here */}
                    {(project.gaRaised50ForDetailing === 1 ||
                      project.gaRaised100ForDetailing === 1) && (
                      <span
                        className="ga-invoice-span-detailing"
                        title="GA Invoice for Detailing"
                      ></span>
                    )}
                    {(project.gaRaised50ForDesign === 1 ||
                      project.gaRaised100ForDesign === 1) && (
                      <span
                        className="ga-invoice-span-design"
                        title="GA Invoice for Design"
                      ></span>
                    )}
                  </div>
                  {project.unclaimed_variation_count !== 0 && (
                    <Link
                      onClick={() => localStorage.setItem("tabIndex", 1)}
                      to={`/project/${project.project_id}/phase/${project.id}`}
                      className={
                        isNavBarOpen
                          ? "corner-triangle"
                          : "corner-triangle-close"
                      }
                    ></Link>
                  )}
                </td>
              );
            case "Phase":
              return (
                <td
                  key="title"
                  style={{ backgroundColor: getColorValue(project.phase_tags) }}
                >
                  {project.title}
                </td>
              );
            case "Project":
              return (
                <td
                  key="projectName"
                  style={{ backgroundColor: getColorValue(project.phase_tags) }}
                >
                  {project.name}
                </td>
              );
            case "Customer":
              return (
                <td key="customerName" title={project.customer_name}>
                  {project?.customer_name?.substring(0, 20)}
                </td>
              );
            case "Team":
              return <td key="team">{project.associated_groups}</td>;
            case "VO(UC)":
              return (
                <td
                  key="unclaimed_variation_count"
                  style={{ textAlign: "center" }}
                >
                  <Link
                    style={{
                      backgroundColor: "transparent",
                      color: "black",
                      marginRight: "0px",
                      top: "0px",
                    }}
                    to={`/project/${project.project_id}/phase/${project.id}`}
                    className="select-projectlist"
                    onClick={() => localStorage.setItem("tabIndex", 1)}
                  >
                    {project.unclaimed_variation_count === 0
                      ? ""
                      : project.unclaimed_variation_count}
                  </Link>
                </td>
              );
            case "VO PHrs(UC)":
              return (
                <td
                  key="unclaimed_variation_hours"
                  style={{ textAlign: "center" }}
                >
                  {project.unclaimed_variation_hours === "00:00"
                    ? ""
                    : project.unclaimed_variation_hours}
                </td>
              );
            case "VO PHrs":
              return (
                <td key="total_variation_hours" style={{ textAlign: "center" }}>
                  {project.total_variation_hours &&
                  project.total_variation_hours !== "00:00"
                    ? project.total_variation_hours
                    : ""}
                </td>
              );
            case "VO AHrs":
              return (
                <td
                  key="total_variation_actual_hours"
                  style={{ textAlign: "center" }}
                >
                  {project.total_variation_actual_hours &&
                  project.total_variation_actual_hours !== "00:00"
                    ? project.total_variation_actual_hours
                    : ""}
                </td>
              );

            case "Planned Hrs":
              return (
                <td key="overall_budget_hours" style={{ textAlign: "center" }}>
                  {project.overall_budget_hours === "00:00"
                    ? ""
                    : project.overall_budget_hours}
                </td>
              );
            case "Actual Hrs":
              const budgetHours = parseTimeToMinutes(
                project.overall_budget_hours
              );
              const actualHours = parseTimeToMinutes(
                project.overall_actual_hours
              );
              let textColor = "black";
              if (budgetHours > 0) {
                const percentage = (actualHours / budgetHours) * 100;
                if (percentage === 100) {
                  textColor = "green";
                } else if (percentage > 99) {
                  textColor = "red";
                } else if (percentage >= 50) {
                  textColor = "#ffa500";
                }
              }
              return (
                <td
                  key="actualHours"
                  style={{
                    textAlign: "center",
                    color: textColor,
                  }}
                >
                  {project.overall_actual_hours === "00:00"
                    ? ""
                    : project.overall_actual_hours}
                </td>
              );
            case "Difference":
              return (
                <td
                  key={"difference_hours"}
                  style={{
                    textAlign: "center",
                    color:
                      convertToMinutes(project.overall_actual_hours) >
                      convertToMinutes(project.overall_budget_hours)
                        ? "red"
                        : "green",
                  }}
                >
                  {project.difference_hours !== "00:00"
                    ? project.difference_hours
                    : ""}
                </td>
              );
            case "DE PHrs":
              return (
                <td key="designBudgetHours" style={{ textAlign: "center" }}>
                  {project.design_budget_hours === "00:00"
                    ? ""
                    : project.design_budget_hours}
                </td>
              );
            case "DE AHrs":
              return (
                <td key="designActualHours" style={{ textAlign: "center" }}>
                  {project.design_actual_hours === "00:00"
                    ? ""
                    : project.design_actual_hours}
                </td>
              );
            case "DO PHrs":
              return (
                <td key="detailingBudgetHours" style={{ textAlign: "center" }}>
                  {project.detailing_budget_hours === "00:00"
                    ? ""
                    : project.detailing_budget_hours}
                </td>
              );
            case "DO AHrs":
              return (
                <td key="detailingActualHours" style={{ textAlign: "center" }}>
                  {project.detailing_actual_hours === "00:00"
                    ? ""
                    : project.detailing_actual_hours}
                </td>
              );
            case "Tags":
              return (
                <td key="phase_tags" style={{ padding: "0px" }}>
                  <select
                    className="project-tags-dropdown"
                    value={project.phase_tags}
                    onChange={(e) =>
                      handleProjectStatusChange(
                        project.id,
                        e.target.value,
                        "phase_tags"
                      )
                    }
                    disabled={!hasPermission("projects", "update")}
                  >
                    <option value="" disabled>
                      Select Tags
                    </option>
                    <option value="Yet to Start">Yet to Start</option>
                    <option value="On Progress">On Progress</option>
                    <option value="Waiting">Waiting</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Completed">Completed</option>
                    <option value="Internal">Internal</option>
                  </select>
                </td>
              );
            case "Scope":
              return (
                <td
                  key="phase_status "
                  style={{
                    padding: "0px",
                    cursor: "pointer",
                  }}
                  ref={stageRef}
                  onClick={() =>
                    setShowScope((prev) => ({
                      ...prev,
                      [project.id]: true,
                    }))
                  }
                >
                  <div
                    className="status-wrapper"
                    style={{
                      marginLeft: "10px",
                      marginRight: "10px",
                      backgroundColor: "transparent",
                    }}
                  >
                    {!showScope[project.id] ? (
                      <div
                        className="status-display"
                        style={{ backgroundColor: "transparent" }}
                        onClick={() =>
                          setShowScope((prev) => ({
                            ...prev,
                            [project.id]: true,
                          }))
                        }
                      >
                        {(() => {
                          let parsedScopes;

                          // Ensure phase_scopes is an array
                          if (Array.isArray(project.phase_scopes)) {
                            parsedScopes = project.phase_scopes;
                          } else if (typeof project.phase_scopes === "string") {
                            parsedScopes = project.phase_scopes
                              .split(",")
                              .map((s) => s.trim());
                          } else {
                            parsedScopes = [];
                          }

                          const visibleScopes = parsedScopes.slice(0, 3); // Show only first 3
                          const remainingCount = parsedScopes.length - 3; // Calculate remaining

                          return parsedScopes.length > 0 ? (
                            <>
                              {visibleScopes.map((status) => {
                                const statusObj = statusOptions.find(
                                  (s) => s.name === status
                                );
                                return (
                                  <span
                                    key={status}
                                    className="status-badge"
                                    style={{
                                      backgroundColor:
                                        statusObj?.color || "white",
                                      marginRight: "5px",
                                      borderRadius: "3px",
                                      padding: "3px",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {status}
                                  </span>
                                );
                              })}
                              {remainingCount > 0 && (
                                <span
                                  className="more-status"
                                  style={{
                                    backgroundColor: "transparent",
                                  }}
                                >
                                  +{remainingCount} more
                                </span>
                              )}
                            </>
                          ) : (
                            <span
                              className="placeholder-text"
                              style={{
                                backgroundColor: "transparent",
                              }}
                            ></span>
                          );
                        })()}
                      </div>
                    ) : (
                      <div
                        className="stage-display-container"
                        style={{ zIndex: "4" }}
                      >
                        <PhaseStatusSelector
                          stageRef={stageRef}
                          project={project}
                          statusOptions={statusOptions}
                          handleProjectScopeChange={handleProjectScopeChange}
                          hasPermission={hasPermission}
                        />
                      </div>
                    )}
                  </div>
                </td>
              );
            case "Priority":
              return (
                <td key="priority" style={{ padding: "0px" }}>
                  <select
                    className="project-priority-dropdown"
                    value={project.priority || ""}
                    onChange={(e) =>
                      handleProjectStatusChange(
                        project.id,
                        e.target.value,
                        "priority"
                      )
                    }
                    disabled={!hasPermission("projects", "update")}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </td>
              );
            case "Manager":
              return (
                <td key="projectManager">{project.project_manager_names}</td>
              );
            case "Start Date":
              return (
                <td key="start_date">
                  {project?.start_date
                    ? format(project?.start_date, "dd-MM-yyyy")
                    : ""}
                </td>
              );
            case "Due Date":
              return (
                <td key="end_date">
                  {project?.end_date
                    ? format(project?.end_date, "dd-MM-yyyy")
                    : ""}
                </td>
              );
            case "INV %":
              return (
                <td
                  key="phase_invoice_claimpercent"
                  style={{ textAlign: "center" }}
                >
                  <Link
                    style={{
                      backgroundColor: "transparent",
                      color: "black",
                      top: "0px",
                      textDecorationLine: "none",
                      fontSize: "12.5px",
                    }}
                    to={`/project/phases/${project.project_id}`}
                  >
                    <span
                      style={{
                        backgroundColor: "transparent",
                        textAlign: "center",
                      }}
                    >
                      {project.phase_invoice_claimpercent === 0
                        ? ""
                        : `${project.phase_invoice_claimpercent}`}
                    </span>
                  </Link>
                </td>
              );
            case "Scheduled(DO)":
              return (
                <td
                  key="scheduledate_do"
                  style={{ padding: "0px", maxWidth: "120px" }}
                  title={
                    project.scheduledate_do_updated_by_name &&
                    project.scheduledate_do_updated_at
                      ? `${project.scheduledate_do_updated_by_name} - ${format(
                          new Date(project.scheduledate_do_updated_at),
                          "dd-MM-yyyy HH:mm"
                        )}`
                      : ""
                  }
                >
                  <DatePicker
                    className="scheduled-date"
                    dateFormat="dd-MM-yyyy"
                    selected={
                      project.scheduledate_do
                        ? typeof project.scheduledate_do === "string"
                          ? parseISO(project.scheduledate_do) // Convert only if it's a string
                          : project.scheduledate_do // Use directly if it's already a Date object
                        : null // Set to null if undefined
                    }
                    onChange={(date) => {
                      handleProjectUpdate("scheduledate_do", date, project.id);
                      setProjects((prevProjects) =>
                        prevProjects.map((p) =>
                          p.id === project.id
                            ? { ...p, scheduledate_do: date }
                            : p
                        )
                      );
                    }}
                    disabled={!hasPermission("projects", "update")}
                  />
                </td>
              );
            case "Scheduled(DE)":
              return (
                <td
                  key="scheduledate_de"
                  style={{ padding: "0px", maxWidth: "120px" }}
                  title={
                    project.scheduledate_de_updated_by_name &&
                    project.scheduledate_de_updated_at
                      ? `${project.scheduledate_de_updated_by_name} - ${format(
                          new Date(project.scheduledate_de_updated_at),
                          "dd-MM-yyyy HH:mm"
                        )}`
                      : ""
                  }
                >
                  <DatePicker
                    className="scheduled-date"
                    dateFormat="dd-MM-yyyy"
                    selected={
                      project.scheduledate_de
                        ? typeof project.scheduledate_de === "string"
                          ? parseISO(project.scheduledate_de) // Convert only if it's a string
                          : project.scheduledate_de // Use directly if it's already a Date object
                        : null // Set to null if undefined
                    }
                    onChange={(date) => {
                      handleProjectUpdate("scheduledate_de", date, project.id);
                      setProjects((prevProjects) =>
                        prevProjects.map((p) =>
                          p.id === project.id
                            ? { ...p, scheduledate_de: date }
                            : p
                        )
                      );
                    }}
                    disabled={!hasPermission("projects", "update")}
                  />
                </td>
              );
            case "Issued On(DO)":
              return (
                <td
                  key="issued_on_do"
                  style={{ padding: "0px", maxWidth: "120px" }}
                  title={
                    project.issued_on_do_updated_by_name &&
                    project.issued_on_do_updated_at
                      ? `${project.issued_on_do_updated_by_name} - ${format(
                          new Date(project.issued_on_do_updated_at),
                          "dd-MM-yyyy HH:mm"
                        )}`
                      : ""
                  }
                >
                  <DatePicker
                    className="issue-date"
                    dateFormat="dd-MM-yyyy"
                    selected={
                      project.issued_on_do
                        ? typeof project.issued_on_do === "string"
                          ? parseISO(project.issued_on_do) // Parse only if it's a string
                          : project.issued_on_do // Use directly if it's already a Date object
                        : null // Return null instead of an empty string
                    }
                    onChange={(date) => {
                      handleProjectUpdate("issued_on_do", date, project.id);
                      setProjects((prevProjects) =>
                        prevProjects.map((p) =>
                          p.id === project.id ? { ...p, issued_on_do: date } : p
                        )
                      );
                    }}
                    disabled={!hasPermission("projects", "update")}
                  />
                </td>
              );
            case "Issued On(DE)":
              return (
                <td
                  key="issued_on_de"
                  style={{ padding: "0px", maxWidth: "120px" }}
                  title={
                    project.issued_on_de_updated_by_name &&
                    project.issued_on_de_updated_at
                      ? `${project.issued_on_de_updated_by_name} - ${format(
                          new Date(project.issued_on_de_updated_at),
                          "dd-MM-yyyy HH:mm"
                        )}`
                      : ""
                  }
                >
                  <DatePicker
                    className="issue-date"
                    dateFormat="dd-MM-yyyy"
                    selected={
                      project.issued_on_de
                        ? typeof project.issued_on_de === "string"
                          ? parseISO(project.issued_on_de) // Parse only if it's a string
                          : project.issued_on_de // Use directly if it's already a Date object
                        : null // Return null instead of an empty string
                    }
                    onChange={(date) => {
                      handleProjectUpdate("issued_on_de", date, project.id);
                      setProjects((prevProjects) =>
                        prevProjects.map((p) =>
                          p.id === project.id ? { ...p, issued_on_de: date } : p
                        )
                      );
                    }}
                    disabled={!hasPermission("projects", "update")}
                  />
                </td>
              );
            case "Remarks (DO)":
              return (
                <td
                  key="remarks_det"
                  style={{ padding: "0px" }}
                  title={
                    project.remarks_det_updated_by_name &&
                    project.remarks_det_updated_at
                      ? `${project.remarks_det} - ${
                          project.remarks_det_updated_by_name
                        } - ${format(
                          new Date(project.remarks_det_updated_at),
                          "dd-MM-yyyy HH:mm"
                        )}`
                      : ""
                  }
                >
                  <textarea
                    disabled={!hasPermission("projects", "update")}
                    type="text"
                    // title={project.remarks}
                    className="project-remarks"
                    maxLength={200}
                    value={editedDetRemarks[project.id] ?? project.remarks_det} // Use edited value or fallback to original
                    onChange={(e) =>
                      setEditedDetRemarks((prev) => ({
                        ...prev,
                        [project.id]: e.target.value, // Track changes per project
                      }))
                    }
                    onBlur={() => {
                      if (
                        editedDetRemarks[project.id] !== undefined &&
                        editedDetRemarks[project.id] !== project.remarks_det
                      ) {
                        handleProjectUpdate(
                          "remarks_det",
                          editedDetRemarks[project.id],
                          project.id
                        );
                      }
                    }}
                  />
                </td>
              );
            case "Remarks (DE)":
              return (
                <td
                  key="remarks_des"
                  style={{ padding: "0px" }}
                  title={
                    project.remarks_des_updated_by_name &&
                    project.remarks_des_updated_at
                      ? `${project.remarks_des} - ${
                          project.remarks_des_updated_by_name
                        } - ${format(
                          new Date(project.remarks_des_updated_at),
                          "dd-MM-yyyy HH:mm"
                        )}`
                      : ""
                  }
                >
                  <textarea
                    disabled={!hasPermission("projects", "update")}
                    type="text"
                    // title={project.remarks}
                    className="project-remarks"
                    maxLength={200}
                    value={editedDesRemarks[project.id] ?? project.remarks_des} // Use edited value or fallback to original
                    onChange={(e) =>
                      setEditedDesRemarks((prev) => ({
                        ...prev,
                        [project.id]: e.target.value, // Track changes per project
                      }))
                    }
                    onBlur={() => {
                      if (
                        editedDesRemarks[project.id] !== undefined &&
                        editedDesRemarks[project.id] !== project.remarks_des
                      ) {
                        handleProjectUpdate(
                          "remarks_des",
                          editedDesRemarks[project.id],
                          project.id
                        );
                      }
                    }}
                  />
                </td>
              );
            default:
              return null;
          }
        }
        return null;
      });
  };

  const groupProjects = (projects) => {
    if (!groupBy) return null;

    const grouped = {};

    projects.forEach((p) => {
      let key = "";
      switch (groupBy) {
        case "Customer":
          key = p.customer_name || "Unknown";
          break;
        case "Team":
          key = p.associated_groups?.[0] || "Unknown";
          break;
        case "Manager":
          key = p.project_manager_names || "Unknown";
          break;
        case "Completed":
          if (p.phase_tags !== "Completed") return; // skip non-completed
          key = "Completed";
          break;
        case "Active":
          if (p.phase_tags === "Completed") return; // skip non-completed
          key = activeProjLink !== "archivedprojects" ? "Active" : "Archived";
          break;
        case "Archived":
          return;
        case "UnclaimedVo":
          if (p.unclaimed_variation_count === 0) return;
          key = "UnclaimedVo";
          break;
        case "UnacceptedVo":
          if (p.unaccepted_variation_count === 0) return;
          key = "UnacceptedVo";
          break;
        case "VO to Confirm":
          if (p.accepted_variation_count === 0) return;
          key = "VO to Confirm";
          break;
        case "Unapproved VO":
          if (p.unapproved_variation_count === 0) return;
          key = "Unapproved VO";
          break;
        case "Open RFIs":
          if (p.open_rfi_count === 0) return;
          key = "Open RFIs";
          break;
        case "Outstanding RFIs":
          if (p.outstanding_rfi_count === 0) return;
          key = "Outstanding RFIs";
          break;
      }

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(p);
    });

    // Return sorted entries
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  };

  const allGroupedProjects =
    groupBy !== "All Projects"
      ? groupProjects(filteredProjects, groupBy)
      : null;

  // Flatten grouped projects if grouping is applied
  const flattenedProjects = allGroupedProjects
    ? Object.values(allGroupedProjects).flat()
    : filteredProjects;

  // Apply pagination on flattened list
  const totalPages = Math.ceil(flattenedProjects.length / projectsPerPage);
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = flattenedProjects.slice(
    indexOfFirstProject,
    indexOfLastProject
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleFirstPage = () => {
    if (currentPage > 1) setCurrentPage(1);
  };
  const handleLastPage = () => {
    if (currentPage < totalPages) setCurrentPage(totalPages);
  };

  const groupedProjects =
    groupBy !== "All Projects" && groupProjects(currentProjects);

  return (
    <div
      className={`${
        userDetails.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin"
          ? "workinprogress-admin-con"
          : "workinprogress-user-con"
      } ${
        isNavBarOpen
          ? "navbar-open-workinprogress"
          : "navbar-closed-workinprogress"
      }`}
    >
      <ToastContainer />
      {hasPermission("projects", "create") && (
        <>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => exportToExcel(flattenedProjects)}
            style={{ top: "3.8rem" }}
            className={
              isNavBarOpen ? "add-exp-heading" : "add-exp-heading-close"
            }
          >
            Export to Excel
          </motion.button>
        </>
      )}
      {hasPermission("projects", "create") && (
        <div className="upload-project-container">
          <input type="file" accept=".csv" />
          <button>Parse CSV</button>
          <a download="sample_projects.csv">
            <button>Download Sample CSV</button>
          </a>
        </div>
      )}
      <div
        className={
          isNavBarOpen ? "project-container" : "project-container-close"
        }
      >
        <div>
          {isFormVisible && (
            <form className="project-column-hide">
              <div className="project-column-hide-container">
                <div className="project-col-swap-header">
                  <h3
                    className="add-project-user-heading"
                    style={{ marginLeft: "14px" }}
                  >
                    Add, Remove, or Swap Columns
                  </h3>
                </div>
                <IoMdClose
                  onClick={toggleFormVisibility}
                  className="close-column-icon"
                />
                <div
                  style={{ marginTop: "45px" }}
                  className="project-col-header-container"
                >
                  <label className="project-column-swap">
                    <input
                      type="checkbox"
                      onChange={() => {
                        const newState = !isAllSelected();

                        const updatedColumns = Object.keys(
                          visibleColumns
                        ).reduce((acc, key) => {
                          // Always keep "Project No" visible
                          acc[key] = key === "Project No" ? true : newState;
                          return acc;
                        }, {});

                        setVisibleColumns(updatedColumns);
                        saveUserPreferences(updatedColumns);
                      }}
                      checked={isAllSelected()}
                      style={{ marginRight: "10px", cursor: "pointer" }}
                    />
                    Select All
                  </label>
                </div>
                {Object.keys(visibleColumns).map((heading, index) => (
                  <div
                    key={heading}
                    className="project-col-header-container"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Checkbox to Show/Hide Column */}
                    <label
                      className="project-column-swap"
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDrop={() => handleDrop(index)}
                      onDragOver={(e) => e.preventDefault()}
                      style={{ flex: 1 }}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[heading]}
                        onChange={() => toggleColumnVisibility(heading)}
                        style={{ marginRight: "10px", cursor: "pointer" }}
                      />
                      {heading}
                    </label>

                    {/* Pin Icon (Right Side) - Click to Set Sticky */}
                    {stickyColumns.includes(heading) ? (
                      <TiPin
                        style={{
                          cursor: "pointer",
                          color: "black",
                          marginLeft: "10px",
                        }}
                        onClick={() => toggleStickyColumn(heading)} // Toggle sticky
                      />
                    ) : (
                      <TiPinOutline
                        style={{
                          cursor: "pointer",
                          color: "gray",
                          marginLeft: "10px",
                        }}
                        onClick={() => toggleStickyColumn(heading)} // Toggle sticky
                      />
                    )}
                  </div>
                ))}
              </div>
            </form>
          )}
        </div>
        <div
          className={
            isNavBarOpen
              ? "project-table-wrapper"
              : "project-table-wrapper-close"
          }
        >
          <table
            ref={tableRef}
            className={
              isNavBarOpen
                ? "workinprogress-table"
                : "workinprogress-table-close"
            }
            style={{ top: "-16px" }}
          >
            <thead
              className="sticky-header"
              style={{ position: "sticky", top: "-1px", zIndex: "2" }}
            >
              <tr>
                <th>
                  <FaChevronDown />
                </th>

                {Object.keys(visibleColumns)
                  .filter((heading) =>
                    userDetails.role_name !== "Super Admin" &&
                    userDetails.role_name !== "Administrator"
                      ? heading !== "INV %"
                      : true
                  )
                  .map((heading, index) =>
                    visibleColumns[heading] ? (
                      <th
                        key={heading}
                        style={{
                          width:
                            heading === "Project No"
                              ? "125px"
                              : columnWidths[index],
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleSort(mapKeyToProjectProperty(heading))
                        }
                        onContextMenu={
                          heading !== "Project No" && heading !== "Project"
                            ? (e) => handleRightClick(e, heading)
                            : undefined
                        }
                      >
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(index, e)}
                        />
                        {heading}
                        {sortConfig.key ===
                          mapKeyToProjectProperty(heading) && (
                          <span>
                            {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                    ) : null
                  )}
                <th
                  style={{
                    position: "sticky",
                    right: "-2px",
                    backgroundColor: "white",
                    borderCollapse: "collapse",
                    textAlign: "center",
                    padding: "5px",
                    zIndex: 4, // Ensure it appears on top
                    width: "20px",
                    borderLeft: "1px solid #9d9d9d",
                  }}
                >
                  <PiColumnsPlusRight
                    className="column-icon"
                    onClick={toggleFormVisibility}
                    title="Add Column"
                    style={{
                      cursor: "pointer",
                      fontSize: "20px",
                      color: "#808080",
                    }} // Optional styling for icon
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {groupBy && groupedProjects
                ? Object.entries(groupedProjects).map(([group, projects]) => (
                    <React.Fragment key={group}>
                      <tr
                        style={{
                          backgroundColor: "white",
                          fontWeight: "bold",
                          border: "1px solid #9d9d9d",
                        }}
                      >
                        <td
                          colSpan={13}
                          style={{ textAlign: "left", padding: "10px" }}
                        >
                          {group}
                        </td>
                      </tr>

                      {projects.map((project) => (
                        <tr
                          key={project.id}
                          style={{
                            border: "none",
                            color: "black",
                            fontWeight: "400",
                            height: "34px",
                            backgroundColor: getColorValue(project.phase_tags),
                          }}
                        >
                          <td
                            style={{
                              backgroundColor: getColorValue(
                                project.phase_tags
                              ),
                            }}
                          >
                            <input
                              disabled={!hasPermission("projects", "delete")}
                              type="checkbox"
                              style={{
                                cursor: hasPermission("projects", "delete")
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                              name={project.name}
                              checked={projectToDelete.some(
                                (p) => p.id === project.id
                              )}
                              onChange={(e) => handleDeleteProject(e, project)}
                            />
                          </td>
                          {renderRowData(project)}
                          <td></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                : currentProjects.map((project, index) => {
                    return (
                      <tr
                        key={project.id}
                        style={{
                          color: "black",
                          fontWeight: "400",
                          height: "34px",
                          backgroundColor: getColorValue(project.phase_tags),
                        }}
                      >
                        <td
                          style={{
                            backgroundColor: getColorValue(project.phase_tags),
                          }}
                        >
                          <input
                            disabled={!hasPermission("projects", "delete")}
                            type="checkbox"
                            style={{
                              cursor: hasPermission("projects", "delete")
                                ? "pointer"
                                : "not-allowed",
                            }}
                            name={project.name}
                            checked={projectToDelete.some(
                              (p) => p.id === project.id
                            )}
                            onChange={(e) => handleDeleteProject(e, project)}
                          />
                        </td>

                        {renderRowData(project)}
                        <td></td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>
      <div
        className={isNavBarOpen ? "legend-container" : "legend-container-close"}
      >
        <p style={{ color: "#ee300099" }}>VO Unclaimed</p>
        <span className="vo-uc"></span>
        <p style={{ color: "Green" }}>DO Uninvoiced</p>
        <span className="do-invoiced"></span>
        <p style={{ color: "Blue" }}>DE Uninvoiced</p>
        <span className="de-invoiced"></span>
      </div>
      {!isFormVisible && (
        <div
          className={
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Super Admin"
              ? "pagination-controls"
              : "pagination-controls-user"
          }
        >
          <h3 style={{ fontWeight: "normal", margin: "0", fontSize: "14px" }}>
            Total : {flattenedProjects.length}
          </h3>
          <select
            className="project-list-row-count"
            value={projectsPerPage}
            onChange={(e) => {
              setProjectsPerPage(Number(e.target.value));
            }}
          >
            <option value="25">25 Records Per Page</option>
            <option value="50">50 Records Per Page</option>
            <option value="75">75 Records Per Page</option>
            <option value="100">100 Records Per Page</option>
          </select>
          <button
            onClick={handleFirstPage}
            disabled={currentPage === 1}
            className="arrow-project-btn"
          >
            <MdKeyboardDoubleArrowLeft title="First Page" />
          </button>
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="arrow-project-btn"
          >
            {/* */}
            <MdKeyboardArrowLeft title="Previous Page" />
          </button>
          <span
            style={{
              textWrap: "nowrap",
            }}
          >
            {paginationCount}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="arrow-project-btn"
          >
            <MdKeyboardArrowRight title="Next Page" />
          </button>
          <button
            onClick={handleLastPage}
            disabled={currentPage === totalPages}
            className="arrow-project-btn"
          >
            <MdKeyboardDoubleArrowRight title="Last Page" />
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkingProgressList;
